import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
    appReducer,
    authSelectors,
    errorActions,
    errorSelectors,
    loadingActions,
    profileActions,
    profileSelectors,
    startupActions,
    userSelectors
} from '@app-root-store';
import { getErrorText, IError, IUser } from '@app-root-interfaces';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { debounce, debounceTime, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, Observable, Subject, timer } from 'rxjs';
import { SiModalService } from '@siemens/element-ng/modal';
import { EnergyRetirementNoticeComponent } from './root/components/energy-retirement-notice/energy-retirement-notice.component';
import { ActivatedRoute, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { templateMigrationNotification } from './shared/constants/notification-text.const';
import { TrackingService } from './root/root-services/tracking.service';
import { ThemeService } from './root/root-services/theme.service';
import { TrackingType } from '@scf-monorepo/interfaces';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private store = inject<Store<appReducer.AppState>>(Store);
    private toastNotificationService = inject(SiToastNotificationService);
    private modalService = inject(SiModalService);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private store$ = inject<Store<appReducer.AppState>>(Store);
    private trackingService = inject(TrackingService);
    private themeService = inject(ThemeService);
    private route = inject(ActivatedRoute);

    public currentUser$: Observable<IUser> = this.store$.pipe(select(userSelectors.selectUserCurrentUser));

    private destroy$: Subject<boolean> = new Subject<boolean>();
    private readonly infoToastTimeoutMs = 15000;
    private previousPath = '';

    ngOnInit() {
        this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang }) => {
            document.documentElement.lang = lang.toLowerCase().split('-')[0];
        });
        this.routeToRedirect();
        this.subscribeToErrors();
        this.subscribeToIsExpired();
        this.showBrowserInfo();
        this.subscribeToProfileChanges();
        this.setUserSettings();
        this.setUpInternalTracking();
        this.subscribeToEnergyTheme();
        this.initApplication();
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private routeToRedirect(): void {
        const url = window.localStorage.getItem('redirectUrl');
        if (url) {
            void this.router.navigateByUrl(url);
            window.localStorage.removeItem('redirectUrl');
        }
    }

    private showBrowserInfo() {
        if ((window as any).chrome) {
            return;
        }
        setTimeout(
            () =>
                this.toastNotificationService.showToastNotification({
                    state: 'warning',
                    title: '',
                    message: this.translateService.instant('old-browser-info'),
                    disableAutoClose: true
                }),
            1000
        ); // wait for translations to load from backend
    }

    private subscribeToErrors() {
        combineLatest([this.store.pipe(select(errorSelectors.selectErrorNext)), this.translateService.onLangChange])
            .pipe(
                takeUntil(this.destroy$),
                filter(([error]) => !!error),
                debounceTime(500),
                switchMap(([error]: [IError, LangChangeEvent]) => {
                    let timeoutMs: number | null = 3000;
                    if (error.timer !== null && error.timer !== undefined) {
                        timeoutMs = error.timer > 0 ? error.timer : null;
                    }

                    console.error(error);
                    const hidden = new Subject<void>();
                    this.toastNotificationService.showToastNotification({
                        state: 'danger',
                        title: '',
                        message: getErrorText(error.text, this.translateService),
                        disableAutoClose: error.isCritical || timeoutMs === null,
                        timeout: timeoutMs ?? undefined,
                        hidden
                    });
                    return hidden.asObservable().pipe(take(1));
                })
            )
            .subscribe(() => {
                this.store.dispatch(errorActions.nextError());
            });
    }

    private subscribeToIsExpired() {
        this.store
            .pipe(
                takeUntil(this.destroy$),
                select(authSelectors.selectAuthTokenExpired),
                filter((isExpired: boolean) => isExpired)
            )
            .subscribe(() => this.openSessionExpiredDialog());
    }

    private openSessionExpiredDialog() {
        // TODO Remove
        // const ref = this.dialog.open(SessionExpiredComponent);
        // ref.afterClosed().subscribe(() => {
        //     this.router.navigate(['auth']);
        // });
    }

    private subscribeToProfileChanges() {
        this.store
            .pipe(
                takeUntil(this.destroy$),
                select(profileSelectors.selectProfileSelectedProfile),
                filter((profile) => !!profile)
            )
            .subscribe(() => this.store.dispatch(profileActions.checkProfileInformations()));
    }

    private setUpInternalTracking() {
        combineLatest([
            this.store$.pipe(
                takeUntil(this.destroy$),
                select(profileSelectors.selectProfileUserProfile),
                filter((profile) => !!profile),
                take(1)
            ),
            this.route.queryParams.pipe(debounce(() => timer(1000)))
        ])
            .pipe(
                switchMap(([profile, params]) =>
                    this.trackingService.addTracking({
                        type: TrackingType.VISIT,
                        referer: params['from'] ?? '',
                        country: profile.country ?? '',
                        department: profile.department ?? '',
                        organization: profile.organization ?? ''
                    })
                )
            )
            .subscribe();
    }

    private async setUserSettings() {
        await this.setDefaultLanguage();

        this.currentUser$
            .pipe(
                takeUntil(this.destroy$),
                filter((user) => !!user && !!user.gid),
                take(1)
            )
            .subscribe((user) => {
                if (!user.settings || !user.settings.hideMigrationNotification) {
                    this.showInfoToast();
                }
                if (user.settings && user.settings.scfLanguage) {
                    this.translateService.use(user.settings.scfLanguage);
                }
            });
    }

    private setDefaultLanguage(): Promise<any> {
        const language = this.translateService.getBrowserLang() || 'en';

        this.translateService.setFallbackLang('en');
        return this.translateService.use(language).toPromise();
    }

    private showInfoToast() {
        this.translateService
            .get([templateMigrationNotification.text, templateMigrationNotification.linkText])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((translations) => {
                const text = translations[templateMigrationNotification.text];
                const linkText = translations[templateMigrationNotification.linkText];
                const link = templateMigrationNotification.link;
                const baseMailtoLink = link.startsWith('mailto:') ? link : `mailto:${link}`;
                const mailSubject = this.translateService.instant('main-file-add-mail-subject');
                const mailContent = this.translateService.instant('main-file-add-mail-content');
                const mailtoLink = `${baseMailtoLink}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailContent)}`;

                this.toastNotificationService.showToastNotification({
                    state: 'info',
                    title: this.translateService.instant('settings-notification'),
                    message: `${text}`,
                    timeout: this.infoToastTimeoutMs,
                    action: {
                        title: linkText,
                        action: () => {
                            window.location.href = mailtoLink;
                        }
                    }
                });
            });
    }

    private initApplication() {
        this.store.dispatch(loadingActions.addLoading({ actionType: 'Initial' }));
        this.store.dispatch(startupActions.startAppInitializer());
        this.store.dispatch(startupActions.getTokenUserData());
        combineLatest([
            this.store.select(authSelectors.selectAuthIsLoggedIn),
            this.store.select(userSelectors.selectUserCurrentUser),
            this.store.select(profileSelectors.selectProfileSelectedProfile)
        ])
            .pipe(
                filter(([isLoggedIn, user, profile]) => {
                    return (isLoggedIn && !!user && !!profile) || (!isLoggedIn && !user && !profile);
                }),
                take(1)
            )
            .subscribe(() => {
                this.store.dispatch(startupActions.finishAppInitializer());
                this.store.dispatch(loadingActions.stopLoading());
            });
    }
    // Only for now until enery has switched to the new system.
    // Can be removed after the switch.
    private subscribeToEnergyTheme() {
        this.themeService.energyThemeSet$
            .pipe(
                takeUntil(this.destroy$),
                take(1), // Only show once per session
                tap(() => {
                    // Small delay to ensure theme is fully loaded
                    setTimeout(() => {
                        this.modalService.show(EnergyRetirementNoticeComponent, {
                            ignoreBackdropClick: false
                        });
                    }, 500);
                })
            )
            .subscribe();
    }
}
